<template>
    <div class="exam-paper">
        <!-- 顶部区域：倒计时和插图 -->
        <div class="exam-header">
            <div class="timer-wrapper">
                <div class="timer-circle">
                    <div class="timer-time">{{ formatTime(countdown) }}</div>
                    <div class="timer-label">倒计时</div>
                </div>
            </div>
        </div>

        <!-- 题目区域 -->
        <div class="question-card">
            <div class="question-header">
                <span class="question-number"><span class="question-number-index">{{ currentQuestion }}</span>/{{ totalQuestions }}</span>
                <div class="question-header-content">
                    <span class="question-type">{{ questionType }}</span>
                    <div class="question-content">
                        {{ currentQuestionData.content }}
                    </div>
                </div>
            </div>
        </div>

        <!-- 选项区域 - 单选题、多选题、判断题 -->
        <div class="options-card" v-if="isChoiceQuestion">
            <div 
                class="option-item" 
                v-for="(option, index) in options" 
                :key="index"
                @click="selectOption(index)"
            >
                <div class="option-checkbox" :class="{ 'checked': isOptionSelected(index) }">
                    <van-icon v-if="isOptionSelected(index)" name="success" color="#2196F3" size="16px" />
                </div>
                <span class="option-label">{{ option.label }}、</span>
                <span class="option-text">{{ option.text }}</span>
            </div>
        </div>

        <!-- 输入区域 - 填空题 -->
        <div class="fill-blank-card" v-if="!isChoiceQuestion && questionType === '填空题'">
            <div class="fill-blank-title">请填写答案</div>
            <div 
                class="fill-blank-item" 
                v-for="(item, index) in fillBlankAnswers" 
                :key="index"
            >
                <span class="fill-blank-number">{{ getCircleNumber(index + 1) }}</span>
                <van-field
                    v-model="fillBlankAnswers[index]"
                    type="text"
                    placeholder="请输入"
                    @input="onFillBlankChange"
                />
            </div>
        </div>

        <!-- 输入区域 - 简答题 -->
        <div class="input-card" v-if="!isChoiceQuestion && questionType === '简答题'">
            <van-field
                v-model="textAnswer"
                type="textarea"
                :placeholder="getInputPlaceholder()"
                rows="6"
                maxlength="1000"
                show-word-limit
                @input="onTextAnswerChange"
            />
        </div>

        <!-- 图片上传区域 - 计算题 -->
        <div class="upload-card" v-if="!isChoiceQuestion && questionType === '计算题'">
            <div class="upload-title">请上传答案</div>
            <div class="upload-area" @click="triggerUpload">
                <van-uploader
                    ref="imageUploader"
                    v-model="uploadedImages"
                    :after-read="onImageRead"
                    :max-count="1"
                    accept="image/*"
                    :preview-full-image="false"
                    style="display: none;"
                />
                <div v-if="uploadedImages.length === 0" class="upload-placeholder">
                    <div class="upload-icon">
                        <van-icon name="plus" size="24px" color="#4DC7D9" />
                    </div>
                    <div class="upload-text">请上传您的答案</div>
                </div>
                <div v-else class="upload-preview">
                    <img :src="uploadedImages[0].content || uploadedImages[0].url" alt="答案图片" />
                    <div class="upload-remove" @click.stop="removeImage">
                        <van-icon name="cross" size="16px" color="#fff" />
                    </div>
                </div>
            </div>
        </div>

        <!-- 导航按钮区域 -->
        <div class="navigation-buttons">
            <button 
                class="nav-btn prev-btn" 
                @click="prevQuestion"
                :disabled="currentQuestion === 1"
            >
                <van-icon name="arrow-left" size="16px" />
                <span>上一题</span>
            </button>
            <button 
                class="nav-btn next-btn" 
                @click="handleNextOrSubmit"
            >
                <span>{{ currentQuestion === totalQuestions ? '提交' : '下一题' }}</span>
                <van-icon name="arrow-left" size="16px" class="arrow-right-icon" />
            </button>
        </div>
    </div>
</template>

<script>
import { getTopics, submitAnswer, uploadImage, submitExam } from '@/api/index'

export default {
    name: 'ExamPaper',
    data() {
        return {
            countdown: 0, // 倒计时，单位：秒（从 Vuex 获取 duration 后初始化）
            timer: null,
            answerId: '', // 答卷ID
            testPaperId: '', // 试卷ID
            questionList: [], // 题目列表
            currentQuestion: 1, // 当前题目索引（从1开始）
            totalQuestions: 0, // 题目总数
            questionType: '', // 题目类型
            selectedOption: null, // 当前选中的选项索引（单选题、判断题）
            selectedOptions: [], // 当前选中的选项索引数组（多选题）
            textAnswer: '', // 文本答案（简答题）
            fillBlankAnswers: [], // 填空题答案数组
            uploadedImages: [], // 上传的图片列表（计算题）
            imageUrl: '', // 上传后的图片URL（计算题）
            answerMap: {}, // 保存每道题的答案 { topicId: answer }
            currentQuestionData: {
                content: '',
                id: ''
            },
            options: []
        };
    },
    created() {
        // 获取路由参数
        this.answerId = this.$route.query.id || '';
        this.testPaperId = this.$route.query.testPaperId || '';
        if (!this.answerId) {
            this.$toast('答卷ID不能为空');
            return;
        }
        if (!this.testPaperId) {
            this.$toast('试卷ID不能为空');
            return;
        }
        
        // 从 Vuex 获取考试时长和开始时间（由 startQuiz 接口统一设置）
        const examDuration = this.$store.state.examDuration || 0;
        const examStartTime = this.$store.state.examStartTime || 0;
        
        if (!examStartTime || !examDuration) {
            this.$toast('考试信息错误，请重新开始考试');
            this.$router.push('/evaluation');
            return;
        }
        
        // 计算剩余时间
        const now = Date.now();
        const elapsedSeconds = Math.floor((now - examStartTime) / 1000); // 已经过的秒数
        const totalSeconds = examDuration * 60; // 总秒数
        const remainingSeconds = totalSeconds - elapsedSeconds; // 剩余秒数
        
        if (remainingSeconds > 0) {
            // 还有剩余时间
            this.countdown = remainingSeconds;
        } else {
            // 时间已用完，自动提交
            this.countdown = 0;
            this.$nextTick(() => {
                this.handleTimeUp();
            });
            return;
        }
        
        // 加载题目数据
        this.loadExamData();
        
        // 开始倒计时
        this.startTimer();
    },
    computed: {
        // 判断是否为选择题（单选题、多选题、判断题）
        isChoiceQuestion() {
            return ['单选题', '多选题', '判断题'].includes(this.questionType);
        },
        // 判断是否为多选题
        isMultipleChoice() {
            return this.questionType === '多选题';
        }
    },
    beforeDestroy() {
        // 清除定时器
        if (this.timer) {
            clearInterval(this.timer);
        }
    },
    methods: {
        // 随机打乱数组（Fisher-Yates 洗牌算法）
        shuffleArray(array) {
            const shuffled = [...array];
            for (let i = shuffled.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
            }
            return shuffled;
        },
        startTimer() {
            this.timer = setInterval(() => {
                if (this.countdown > 0) {
                    this.countdown--;
                } else {
                    // 时间到，自动提交
                    this.handleTimeUp();
                }
            }, 1000);
        },
        formatTime(seconds) {
            const minutes = Math.floor(seconds / 60);
            const secs = seconds % 60;
            return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
        },
        // 判断选项是否被选中
        isOptionSelected(index) {
            if (this.isMultipleChoice) {
                // 多选题：检查索引是否在选中数组中
                return this.selectedOptions.includes(index);
            } else {
                // 单选题、判断题：检查索引是否等于选中索引
                return this.selectedOption === index;
            }
        },
        selectOption(index) {
            if (this.isMultipleChoice) {
                // 多选题：切换选中状态
                const optionIndex = this.selectedOptions.indexOf(index);
                if (optionIndex > -1) {
                    // 已选中，取消选中
                    this.selectedOptions.splice(optionIndex, 1);
                } else {
                    // 未选中，添加到选中数组
                    this.selectedOptions.push(index);
                }
                // 保存当前题目的答案（多选题保存为数组）
                this.saveChoiceAnswer();
            } else {
                // 单选题、判断题：单选
                this.selectedOption = index;
                // 保存当前题目的答案
                if (this.currentQuestionData.id) {
                    const selectedAnswer = this.options[index] ? this.options[index].text : '';
                    this.answerMap[this.currentQuestionData.id] = selectedAnswer;
                }
            }
        },
        // 保存选择题答案
        saveChoiceAnswer() {
            if (!this.currentQuestionData.id) {
                return;
            }
            
            if (this.isMultipleChoice) {
                // 多选题：保存选中选项的文本数组
                const selectedAnswers = this.selectedOptions
                    .map(idx => this.options[idx] ? this.options[idx].text : '')
                    .filter(text => text);
                this.answerMap[this.currentQuestionData.id] = selectedAnswers;
            } else {
                // 单选题、判断题：保存单个选项文本
                if (this.selectedOption !== null) {
                    const selectedAnswer = this.options[this.selectedOption] ? this.options[this.selectedOption].text : '';
                    this.answerMap[this.currentQuestionData.id] = selectedAnswer;
                }
            }
        },
        // 文本答案输入（简答题）
        onTextAnswerChange() {
            // 保存当前题目的答案（v-model会自动更新textAnswer）
            if (this.currentQuestionData.id) {
                this.answerMap[this.currentQuestionData.id] = this.textAnswer;
            }
        },
        // 填空题答案输入
        onFillBlankChange() {
            // 保存当前题目的答案（v-model会自动更新fillBlankAnswers）
            if (this.currentQuestionData.id) {
                this.answerMap[this.currentQuestionData.id] = this.fillBlankAnswers;
            }
        },
        // 获取带圈数字
        getCircleNumber(num) {
            const circleNumbers = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩'];
            return circleNumbers[num - 1] || `(${num})`;
        },
        // 触发图片上传
        triggerUpload() {
            const uploaderInput = this.$refs.imageUploader.$el.querySelector('input');
            if (uploaderInput) {
                uploaderInput.click();
            }
        },
        // 图片读取后处理
        async onImageRead(file) {
            if (!file.file) {
                return;
            }
            
            // 显示上传中提示
            this.$toast.loading({
                message: '上传中...',
                forbidClick: true,
                duration: 0
            });
            
            try {
                // 上传图片
                const res = await uploadImage(file.file);
                this.$toast.clear();
                
                if (res.code == 200 && res.data) {
                    // 上传成功，保存图片URL
                    const imageUrl = res.data.url || res.data;
                    this.imageUrl = imageUrl;
                    // 更新uploadedImages，确保有url字段用于显示
                    if (this.uploadedImages.length > 0) {
                        this.$set(this.uploadedImages[0], 'url', imageUrl);
                    }
                    // 保存当前题目的答案
                    if (this.currentQuestionData.id) {
                        this.answerMap[this.currentQuestionData.id] = imageUrl;
                    }
                    this.$toast.success('上传成功');
                } else {
                    this.$toast('上传失败，请重试');
                    // 移除失败的图片
                    this.uploadedImages = [];
                    this.imageUrl = '';
                }
            } catch (error) {
                this.$toast.clear();
                console.error('上传图片失败:', error);
                this.$toast('上传失败，请重试');
                // 移除失败的图片
                this.uploadedImages = [];
                this.imageUrl = '';
            }
        },
        // 移除图片
        removeImage() {
            this.uploadedImages = [];
            this.imageUrl = '';
            // 清除保存的答案
            if (this.currentQuestionData.id) {
                delete this.answerMap[this.currentQuestionData.id];
            }
        },
        // 获取输入框占位符
        getInputPlaceholder() {
            if (this.questionType === '填空题') {
                return '请输入答案';
            } else if (this.questionType === '简答题') {
                return '请输入答案内容';
            } else if (this.questionType === '计算题') {
                return '请输入计算过程和答案';
            }
            return '请输入答案';
        },
        async prevQuestion() {
            if (this.currentQuestion > 1) {
                this.currentQuestion--;
                this.loadQuestionData();
            }
        },
        async handleNextOrSubmit() {
            // 每次操作都要调用答题接口
            if (this.currentQuestionData.id) {
                await this.submitCurrentAnswer();
            }
            
            // 如果是最后一题，显示确认对话框
            if (this.currentQuestion === this.totalQuestions) {
                // 显示确认提交对话框
                this.showSubmitConfirm();
            } else {
                // 不是最后一题，切换到下一题
                if (this.currentQuestion < this.totalQuestions) {
                    this.currentQuestion++;
                    this.loadQuestionData();
                }
            }
        },
        // 显示提交确认对话框
        showSubmitConfirm() {
            this.$dialog.confirm({
                title: '请认真核对答题,确认交卷',
                message: '',
                confirmButtonText: '确认交卷',
                cancelButtonText: '取消',
                confirmButtonColor: '#FF6633',
                className: 'submit-confirm-dialog',
                showCancelButton: true
            }).then(() => {
                // 点击确认交卷
                this.confirmSubmitExam();
            }).catch(() => {
                // 点击取消，不做任何操作
            });
        },
        // 确认提交试卷（需要校验未答题目）
        async confirmSubmitExam() {
            // 如果倒计时还没结束，需要校验是否有未答题目
            if (this.countdown > 0) {
                const unansweredQuestions = this.checkUnansweredQuestions();
                if (unansweredQuestions.length > 0) {
                    this.$dialog.alert({
                        title: '提示',
                        message: `还有 ${unansweredQuestions.length} 道题目未作答，请完成后再提交`
                    });
                    return;
                }
            }
            
            // 校验通过，提交试卷（mandatorySubmission: false，会检测未答题目）
            await this.submitExam(false);
        },
        // 校验未答题目
        checkUnansweredQuestions() {
            const unanswered = [];
            
            this.questionList.forEach((question, index) => {
                const questionId = question.id || '';
                const questionType = question.type || '';
                const savedAnswer = this.answerMap[questionId];
                
                // 检查是否有答案
                let hasAnswer = false;
                
                if (['单选题', '多选题', '判断题'].includes(questionType)) {
                    // 选择题：检查是否有选中选项
                    if (questionType === '多选题') {
                        hasAnswer = Array.isArray(savedAnswer) && savedAnswer.length > 0;
                    } else {
                        hasAnswer = savedAnswer !== undefined && savedAnswer !== null && savedAnswer !== '';
                    }
                } else if (questionType === '填空题') {
                    // 填空题：检查答案数组是否有内容
                    hasAnswer = Array.isArray(savedAnswer) && savedAnswer.some(text => text && text.trim());
                } else if (questionType === '简答题') {
                    // 简答题：检查是否有文本答案
                    hasAnswer = savedAnswer && savedAnswer.trim();
                } else if (questionType === '计算题') {
                    // 计算题：检查是否有图片URL
                    hasAnswer = savedAnswer && savedAnswer.trim();
                }
                
                if (!hasAnswer) {
                    unanswered.push(index + 1);
                }
            });
            
            return unanswered;
        },
        async nextQuestion() {
            // 如果当前题目有答案，先提交答案
            if (this.currentQuestionData.id) {
                if (this.isChoiceQuestion) {
                    // 选择题：检查是否选择了选项
                    if (this.isMultipleChoice) {
                        // 多选题：检查是否有选中的选项
                        if (this.selectedOptions.length > 0) {
                            await this.submitCurrentAnswer();
                        }
                    } else {
                        // 单选题、判断题：检查是否选择了选项
                        if (this.selectedOption !== null) {
                            await this.submitCurrentAnswer();
                        }
                    }
                } else {
                    // 填空题、简答题、计算题：检查是否有答案
                    if (this.questionType === '填空题') {
                        // 填空题：检查是否有填空答案
                        if (this.fillBlankAnswers && this.fillBlankAnswers.some(text => text && text.trim())) {
                            await this.submitCurrentAnswer();
                        }
                    } else if (this.questionType === '简答题') {
                        // 简答题：检查是否有文本答案
                        if (this.textAnswer && this.textAnswer.trim()) {
                            await this.submitCurrentAnswer();
                        }
                    } else if (this.questionType === '计算题') {
                        // 计算题：检查是否有上传图片
                        if (this.imageUrl) {
                            await this.submitCurrentAnswer();
                        }
                    }
                }
            }
            
            // 切换到下一题
            if (this.currentQuestion < this.totalQuestions) {
                this.currentQuestion++;
                this.loadQuestionData();
            }
        },
        async submitCurrentAnswer() {
            if (!this.currentQuestionData.id) {
                return;
            }
            
            // 如果没有 answerId，无法提交答案
            if (!this.answerId) {
                console.warn('答卷ID为空，无法提交答案');
                return;
            }
            
            let answer = [];
            
            if (this.isChoiceQuestion) {
                // 选择题：提交选项文本数组
                if (this.isMultipleChoice) {
                    // 多选题：提交选中选项的文本数组
                    if (this.selectedOptions.length > 0) {
                        answer = this.selectedOptions
                            .map(idx => this.options[idx] ? this.options[idx].text : '')
                            .filter(text => text);
                    }
                } else {
                    // 单选题、判断题：提交数组，包含单个选项文本
                    if (this.selectedOption !== null) {
                        const selectedAnswer = this.options[this.selectedOption] ? this.options[this.selectedOption].text : '';
                        if (selectedAnswer) {
                            answer = [selectedAnswer];
                        }
                    }
                }
            } else {
                // 填空题、简答题、计算题
                if (this.questionType === '填空题') {
                    // 填空题：提交数组，包含所有填空答案
                    if (this.fillBlankAnswers && this.fillBlankAnswers.length > 0) {
                        // 过滤掉空白答案
                        answer = this.fillBlankAnswers
                            .map(text => text ? text.trim() : '')
                            .filter(text => text);
                    }
                } else if (this.questionType === '简答题') {
                    // 简答题：提交数组，包含文本答案
                    if (this.textAnswer && this.textAnswer.trim()) {
                        answer = [this.textAnswer.trim()];
                    }
                } else if (this.questionType === '计算题') {
                    // 计算题：提交图片URL
                    if (this.imageUrl) {
                        answer = [this.imageUrl];
                    }
                }
            }
            
            // 如果 answer 为空数组，说明没有答题，无需调用接口
            if (answer.length === 0) {
                return;
            }
            
            try {
                const data = {
                    answerId: this.answerId,
                    topicId: this.currentQuestionData.id,
                    answer: answer
                };
                
                await submitAnswer(data);
                // 提交成功，静默处理，不显示提示
            } catch (error) {
                console.error('提交答案失败:', error);
                // 提交失败也不阻止切换题目，只记录错误
            }
        },
        loadQuestionData() {
            // 根据 currentQuestion 从题目列表中加载当前题目数据
            if (this.questionList.length === 0) {
                return;
            }
            
            const questionIndex = this.currentQuestion - 1;
            if (questionIndex < 0 || questionIndex >= this.questionList.length) {
                return;
            }
            
            const question = this.questionList[questionIndex];
            
            // 更新当前题目数据
            this.currentQuestionData = {
                content: question.content || question.title || '',
                id: question.id || question.topicId || ''
            };
            
            // 更新题目类型
            this.questionType = question.type || question.questionType || '单选题';
            
            // 处理填空题答案数量
            if (this.questionType === '填空题') {
                const answerCount = question.answer && Array.isArray(question.answer) ? question.answer.length : 1;
                // 初始化填空题答案数组
                this.fillBlankAnswers = new Array(answerCount).fill('');
            }
            
            // 处理选项数据
            if (question.options && Array.isArray(question.options)) {
                this.options = question.options.map((opt, index) => {
                    return {
                        label: String.fromCharCode(65 + index), // 生成字母标签 A-Z
                        text: opt.text || opt.content || opt,
                        value: opt.value || opt.id || index
                    };
                });
            } else if (question.optionList && Array.isArray(question.optionList)) {
                this.options = question.optionList.map((opt, index) => {
                    return {
                        label: String.fromCharCode(65 + index), // 生成字母标签 A-Z
                        text: opt.text || opt.content || opt,
                        value: opt.value || opt.id || index
                    };
                });
            } else {
                // 如果没有选项数据，设置默认选项（判断题）
                this.options = [
                    { label: 'A', text: '对', value: 1 },
                    { label: 'B', text: '错', value: 0 }
                ];
            }
            
            // 恢复之前保存的答案
            const savedAnswer = this.answerMap[this.currentQuestionData.id];
            if (savedAnswer !== undefined) {
                if (this.isChoiceQuestion) {
                    if (this.isMultipleChoice) {
                        // 多选题：恢复多个选项
                        if (Array.isArray(savedAnswer)) {
                            // 如果保存的是数组，查找对应的选项索引
                            this.selectedOptions = savedAnswer
                                .map(answerText => {
                                    const index = this.options.findIndex(opt => opt.text === answerText);
                                    return index >= 0 ? index : -1;
                                })
                                .filter(index => index >= 0);
                        } else if (typeof savedAnswer === 'string' && savedAnswer.includes(',')) {
                            // 如果是逗号分隔的字符串，先分割成数组
                            const answerArray = savedAnswer.split(',').map(s => s.trim()).filter(s => s);
                            this.selectedOptions = answerArray
                                .map(answerText => {
                                    const index = this.options.findIndex(opt => opt.text === answerText);
                                    return index >= 0 ? index : -1;
                                })
                                .filter(index => index >= 0);
                        } else {
                            // 兼容旧数据格式（单个答案）
                            const optionIndex = this.options.findIndex(opt => opt.text === savedAnswer);
                            this.selectedOptions = optionIndex >= 0 ? [optionIndex] : [];
                        }
                    } else {
                        // 单选题、判断题：查找对应的选项索引
                        const optionIndex = this.options.findIndex(opt => opt.text === savedAnswer);
                        this.selectedOption = optionIndex >= 0 ? optionIndex : null;
                    }
                } else {
                    // 填空题、简答题、计算题
                    if (this.questionType === '填空题') {
                        // 填空题：恢复填空答案数组
                        if (Array.isArray(savedAnswer)) {
                            this.fillBlankAnswers = [...savedAnswer];
                        } else {
                            // 兼容旧数据格式
                            this.fillBlankAnswers = [savedAnswer];
                        }
                    } else if (this.questionType === '简答题') {
                        // 简答题：恢复文本答案
                        this.textAnswer = savedAnswer;
                    } else if (this.questionType === '计算题') {
                        // 计算题：恢复图片URL
                        this.imageUrl = savedAnswer;
                        if (this.imageUrl) {
                            this.uploadedImages = [{
                                url: this.imageUrl,
                                content: this.imageUrl
                            }];
                        } else {
                            this.uploadedImages = [];
                        }
                    }
                }
            } else {
                // 如果没有保存的答案，重置状态
                if (this.isChoiceQuestion) {
                    if (this.isMultipleChoice) {
                        this.selectedOptions = [];
                    } else {
                        this.selectedOption = null;
                    }
                } else {
                    if (this.questionType === '填空题') {
                        // 填空题：已在上面初始化
                    } else if (this.questionType === '简答题') {
                        this.textAnswer = '';
                    } else if (this.questionType === '计算题') {
                        this.textAnswer = '';
                        this.imageUrl = '';
                        this.uploadedImages = [];
                    }
                }
            }
        },
        async loadExamData() {
            try {
                this.$toast.loading({
                    message: '加载中...',
                    forbidClick: true,
                    duration: 0
                });
                
                const res = await getTopics(this.testPaperId);
                
                this.$toast.clear();
                
                if (res.code === 200 && res.data) {
                    // 处理返回的数据，随机打乱题目顺序
                    this.questionList = this.shuffleArray([...res.data]);
                    
                    // 如果 category 是简答题或计算题，将 type 赋值为对应的值
                    this.questionList.forEach(question => {
                        if (question.category === '简答题' || question.category === '计算题') {
                            question.type = question.category;
                        }
                    });
                    
                    // 设置题目总数
                    this.totalQuestions = this.questionList.length;
                    
                    // 设置当前题目为第一题
                    this.currentQuestion = 1;
                    
                    // 加载第一题数据
                    this.loadQuestionData();
                } else {
                    this.$toast('获取题目列表失败');
                }
            } catch (error) {
                this.$toast.clear();
                console.error('加载题目列表失败:', error);
                this.$toast('加载题目列表失败，请稍后重试');
            }
        },
        handleTimeUp() {
            clearInterval(this.timer);
            // 倒计时结束，自动提交（mandatorySubmission: true，不检测未答题目）
            this.submitExam(true, true);
        },
        async submitExam(mandatorySubmission = false, isTimeUp = false) {
            if (!this.answerId) {
                this.$toast('答卷ID不能为空');
                return;
            }
            
            try {
                this.$toast.loading({
                    message: '提交中...',
                    forbidClick: true,
                    duration: 0
                });
                
                const data = {
                    answerId: this.answerId,
                    mandatorySubmission: mandatorySubmission
                };
                
                const res = await submitExam(data);
                this.$toast.clear();
                
                if (res.code === 200) {
                    // 提交成功
                    if (isTimeUp) {
                        // 倒计时结束自动提交
                        this.$dialog.alert({
                            title: '提示',
                            message: '答卷提交'
                        }).then(() => {
                            // 清除考试信息
                            this.$store.commit('clearExamInfo');
                            // 返回到 evaluation 页面
                            this.$router.push('/evaluation');
                        });
                    } else {
                        // 手动提交
                        this.$toast.success('提交成功');
                        // 清除考试信息
                        this.$store.commit('clearExamInfo');
                        // 返回到 evaluation 页面
                        this.$router.push('/evaluation');
                    }
                } else {
                    this.$toast(res.msg || '提交失败，请重试');
                }
            } catch (error) {
                this.$toast.clear();
                console.error('提交试卷失败:', error);
                this.$toast(error.msg || '提交失败，请稍后重试');
            }
        }
    }
};
</script>

<style scoped lang="scss">
.exam-paper {
    box-sizing: border-box;
    min-height: 100%;
    padding-bottom: 50px;
    background: linear-gradient(to bottom, #E3F2FD, #F5F5F5);
}

.exam-header {
    box-sizing: border-box;
    position: relative;
    height: 185px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 16px;
    overflow: hidden;
    background: url('~@/assets/image/exam-top-bg.png') no-repeat center center;
    background-size: 100% 100%;

    .timer-wrapper {
        position: relative;
        z-index: 2;

        .timer-circle {
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;

            .timer-time {
                font-size: 28px;
                font-weight: bold;
                color: #fff;
                margin-bottom: 4px;
                position: relative;
                z-index: 1;
            }

            .timer-label {
                font-size: 14px;
                color: #fff;
                position: relative;
                z-index: 1;
            }
        }
    }
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.question-card {
    margin: 20px 16px;
    padding: 20px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .question-header {
        display: flex;
        align-items: flex-start;
        margin-bottom: 16px;

        .question-number {
            margin-top: 4px;
            height: 20px;
            font-size: 14px;
            color: #999;
            margin-right: 12px;
            .question-number-index{
                font-size: 20px;
                font-weight: bold;
                color: #333;
            }
        }
        .question-header-content{
            flex: 1;
            .question-type {
                padding: 4px;
                display: inline;
                font-size: 12px;
                border-radius: 4px;
                text-align: center;
                line-height: 20px;
                background: #4DC7D9;
                color: #fff;
            }
            .question-content {
                display: inline;
                font-size: 16px;
                color: #333;
                line-height: 29px;
            }
        }
    }
}

.options-card {
    margin: 0 16px 20px;
    padding: 16px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .option-item {
        display: flex;
        align-items: center;
        padding: 12px 0;
        border-bottom: 1px solid #F5F5F5;
        cursor: pointer;

        &:last-child {
            border-bottom: none;
        }

        .option-checkbox {
            width: 20px;
            height: 20px;
            border: 2px solid #ddd;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 12px;
            flex-shrink: 0;
            transition: all 0.3s;

            &.checked {
                background: #E3F2FD;
                border-color: #2196F3;
            }
        }

        .option-label {
            font-size: 16px;
            color: #333;
            font-weight: 500;
            margin-right: 4px;
        }

        .option-text {
            font-size: 16px;
            color: #333;
        }
    }
}

.navigation-buttons {
    position: absolute;
    box-sizing: border-box;
    left: 0;
    bottom: 0;
    padding: 0 16px;
    width: 100%;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fff;
    .nav-btn {
        width: 124px;
        height: 40px;
        border-radius: 20px;
        border: none;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;

        span {
            display: inline-block;
        }

        .arrow-icon {
            font-size: 16px;
            line-height: 1;
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        &.prev-btn {
            background: #B3E5FC;
            color: #4FC3F7;

            .arrow-icon {
                color: #4FC3F7;
            }

            &:not(:disabled):active {
                background: #81D4FA;
            }
        }

        &.next-btn {
            background: #FF6633;
            color: #fff;

            .arrow-icon {
                color: #fff;
            }

            .arrow-right-icon {
                transform: rotate(180deg);
                display: inline-block;
            }

            &:not(:disabled):active {
                background: #FB8C00;
            }
        }
    }
}

.input-card {
    margin: 0 16px 20px;
    padding: 16px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    ::v-deep .van-field {
        padding: 0;
    }

    ::v-deep .van-field__control {
        min-height: 120px;
        font-size: 16px;
        color: #333;
        line-height: 1.6;
    }
}

.fill-blank-card {
    margin: 0 16px 20px;
    padding: 16px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .fill-blank-title {
        font-size: 16px;
        font-weight: 500;
        color: #333;
        margin-bottom: 16px;
    }

    .fill-blank-item {
        display: flex;
        align-items: center;
        margin-bottom: 16px;

        &:last-child {
            margin-bottom: 0;
        }

        .fill-blank-number {
            font-size: 16px;
            color: #333;
            margin-right: 12px;
            flex-shrink: 0;
        }

        ::v-deep .van-cell {
            flex: 1;
            padding: 10px 16px;
            border: 1px solid #E5E5E5;
            border-radius: 8px;
            background: #FAFAFA;

            &::after {
                display: none;
            }
        }

        ::v-deep .van-field__control {
            font-size: 16px;
            color: #333;
        }
    }
}

.upload-card {
    margin: 0 16px 20px;
    padding: 16px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .upload-title {
        font-size: 16px;
        font-weight: 500;
        color: #333;
        margin-bottom: 16px;
    }

    .upload-area {
        position: relative;
        width: 100%;
        height: 200px;
        border: 2px dashed #4DC7D9;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        background: #FAFAFA;
        transition: all 0.3s;

        &:active {
            background: #F0F0F0;
        }

        .upload-placeholder {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 40px 20px;

            .upload-icon {
                width: 48px;
                height: 48px;
                border: 2px dashed #4DC7D9;
                border-radius: 4px;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 12px;
            }

            .upload-text {
                font-size: 14px;
                color: #999;
            }
        }

        .upload-preview {
            position: relative;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 16px;

            img {
                max-width: 100%;
                max-height: 400px;
                object-fit: contain;
                border-radius: 4px;
            }

            .upload-remove {
                position: absolute;
                top: 8px;
                right: 8px;
                width: 24px;
                height: 24px;
                background: rgba(0, 0, 0, 0.6);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                transition: all 0.3s;

                &:active {
                    background: rgba(0, 0, 0, 0.8);
                }
            }
        }
    }
}

// 提交确认对话框样式
::v-deep .submit-confirm-dialog {
    .van-dialog__header {
        padding-top: 30px;
        padding-bottom: 20px;
        
        .van-dialog__title {
            font-size: 18px;
            font-weight: bold;
            color: #333;
        }
    }
    
    .van-dialog__message {
        padding: 20px 16px;
        text-align: center;
        min-height: 100px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    
    .van-dialog__footer {
        padding: 16px;
        
        .van-button {
            height: 44px;
            font-size: 16px;
            border-radius: 8px;
            
            &--default {
                background: #fff;
                color: #333;
                border: 1px solid #E5E5E5;
            }
            
            &--danger {
                background: #FF6633;
                color: #fff;
                border: none;
            }
        }
    }
}
</style>

